/**
 * What this client declares about itself on every API request: the access protocol it speaks,
 * the Marvin output it renders, whether it can hold a URL route, and which build it is.
 *
 * ai_engine reads these into one request context (`app/client_context.py`). They are
 * declarations, never authority: the server decides access, and a forged value can only make
 * the sender's own responses worse. `clientDeclarationHeaders` is the one place they are
 * made; the Axios interceptor and the fetch wrapper (`util/authenticatedFetch.ts`, which also
 * opens every SSE stream) both apply it, so no call site stamps a header of its own. The iOS
 * shell's URLSession sends the same identity from Swift (`ClientDeclaration.apply`).
 */
import { MARVIN_RENDERS_HEADER } from "./marvinRenders";
import type { Platform } from "../platform/Platform";
import { getPlatform } from "../platform/platformRef";

/** The build that sent the request: `ios 2.71.33 (412) g1a2b3c4`, `web g1a2b3c4`, `desktop 0.2.0 g1a2b3c4`. */
export const CLIENT_HEADER = "X-Primepoint-Client";
/** Sent as "1" by a host that can run on a `/{company}/{project}` URL and hold that route's
 *  credential (`projectAdmission.supportsUrlRouting`); ai_engine offers URL routing on
 *  `/session/me` only to a request that carries it. */
export const URL_ROUTING_HEADER = "X-Primepoint-Url-Routing";

/** The commit as a build names it: its first eight hex digits, or nothing for a local build. */
function commitSuffix(commit: string | undefined): string {
    const sha = commit?.trim().toLowerCase() ?? "";
    return /^[0-9a-f]{7,40}$/.test(sha) ? ` g${sha.slice(0, 8)}` : "";
}

/** The browser build: `web g<sha>` (the commit CI built, `REACT_APP_GIT_COMMIT`). */
export function webClientIdentity(commit: string | undefined): string {
    return `web${commitSuffix(commit)}`;
}

/** The desktop build: `desktop <version> g<sha>`; `null` when the version is unknown. */
export function desktopClientIdentity(
    version: string | undefined,
    commit: string | undefined,
): string | null {
    return version && /^\d{1,4}(\.\d{1,4}){0,3}$/.test(version)
        ? `desktop ${version}${commitSuffix(commit)}`
        : null;
}

type DeclaringHost = Pick<Platform, "marvinRenders" | "clientIdentity"> & {
    projectAdmission: Pick<Platform["projectAdmission"], "supportsUrlRouting">;
};

/** Every declaration this host makes, as headers. Pure: the host is the only input. */
export function clientDeclarationHeaders(host: DeclaringHost): Record<string, string> {
    return {
        "X-Access-Client": "1",
        "X-Access-Enforcer": "3",
        [MARVIN_RENDERS_HEADER]: host.marvinRenders.join(","),
        ...(host.projectAdmission.supportsUrlRouting ? { [URL_ROUTING_HEADER]: "1" } : {}),
        ...(host.clientIdentity ? { [CLIENT_HEADER]: host.clientIdentity } : {}),
    };
}

interface HeaderCarrier {
    set(name: string, value: string): unknown;
    delete(name: string): unknown;
}

/** Sets the installed host's declarations on an outgoing request, replacing a caller's own
 *  copy; a declaration the host does not make is removed. */
export function applyClientDeclarationHeaders(
    headers: HeaderCarrier,
    host: DeclaringHost = getPlatform(),
): void {
    const declared = clientDeclarationHeaders(host);
    for (const name of [URL_ROUTING_HEADER, CLIENT_HEADER]) {
        if (!(name in declared)) headers.delete(name);
    }
    for (const [name, value] of Object.entries(declared)) headers.set(name, value);
}
